import { ReactNode } from 'react';

// @mui material components
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Icon from '@mui/material/Icon';

// Material Dashboard 2 PRO React TS components
import MDBox from '@components/MDBox';

// Custom styles for the SidenavCollapse
import {
  collapseItem,
  collapseIconBox,
  collapseText,
  collapseArrow
} from '@components/Sidenav/styles/sidenavCollapse';

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from '@context/MDContext';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Tooltip } from '@mui/material';
import { Colors } from '@constants/theme';
import FaIcon from '@components/FaIcon';

// Declaring props types for SidenavCollapse
interface Props {
  icon: IconProp;
  name: string;
  miniSideNav: boolean;
  children?: ReactNode;
  active?: Boolean;
  noCollapse?: Boolean;
  open?: Boolean;
  [key: string]: any;
  tooltip?: string;
  padding?: string;
  width?: string;
}

function SidenavCollapse({
  icon,
  name,
  children,
  active,
  noCollapse,
  open,
  tooltip,
  padding,
  width,
  miniSideNav,
  ...rest
}: Props): JSX.Element {
  const [controller] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } =
    controller;

  return (
    <>
      <ListItem
        component='li'
        sx={{
          paddingLeft: padding ? padding : `${miniSideNav ? '26px' : '10px'}`,
          width: `${miniSideNav ? '45%' : width ?? '100%'}`
        }}
      >
        <Tooltip
          title={tooltip ? tooltip : ''}
          placement={tooltip ? 'right' : 'top'}
        >
          <MDBox
            {...rest}
            color='info'
            sx={(theme: any) =>
              collapseItem(theme, {
                active,
                transparentSidenav,
                whiteSidenav,
                darkMode
              })
            }
            display={'flex'}
            justifyContent={'center'}
          >
            <ListItemIcon
              sx={(theme) =>
                collapseIconBox(theme, {
                  transparentSidenav,
                  whiteSidenav,
                  darkMode,
                  color: Colors.INFO
                })
              }
            >
              <FaIcon icon={icon} typographyProps={{ variant: 'sm' }} />
            </ListItemIcon>

            <ListItemText
              primary={name}
              sx={(theme) =>
                collapseText(theme, {
                  miniSidenav,
                  transparentSidenav,
                  whiteSidenav,
                  active,
                  color: 'info'
                })
              }
            />

            <Icon
              sx={(theme) =>
                collapseArrow(theme, {
                  noCollapse,
                  transparentSidenav,
                  whiteSidenav,
                  miniSidenav,
                  open,
                  active,
                  darkMode
                })
              }
            >
              expand_less
            </Icon>
          </MDBox>
        </Tooltip>
      </ListItem>
      {children && (
        <Collapse in={Boolean(open)} unmountOnExit>
          {children}
        </Collapse>
      )}
    </>
  );
}

// Declaring default props for SidenavCollapse
SidenavCollapse.defaultProps = {
  active: false,
  noCollapse: false,
  children: false,
  open: false
};

export default SidenavCollapse;
